/**
 * WRMS Reports Page — Digi Sahyadri
 * Leave, Early Leave, Gate Pass, Extra Work reports with export
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/common/Sidebar';
import Navbar from '../../components/common/Navbar';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { 
  FaFileExport, 
  FaSync, 
  FaExternalLinkAlt, 
  FaSearch, 
  FaClipboardList, 
  FaCheck, 
  FaClock, 
  FaTimes, 
  FaBan, 
  FaCalendarAlt 
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getAllRequests, getRequestTypeLabel } from '../services/wrmsService';
import { isDeptVisibleToUser } from '../../utils/departmentVisibility';

import '../css/wrms.css';

const REPORT_TABS = [
  { id: 'leave', label: 'Leave Reports' },
  { id: 'early_leave', label: 'Early Leave Reports' },
  { id: 'gate_pass', label: 'Gate Pass Reports' },
  { id: 'extra_work', label: 'Extra Work Reports' },
];

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const WRMSReportsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeReport, setActiveReport] = useState('leave');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deptFilter, setDeptFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = await getAllRequests({ requestType: activeReport });
      setRequests(reqs);
    } catch {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [activeReport]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = requests.filter(r => {
    const ms = !search || r.employeeName?.toLowerCase().includes(search.toLowerCase()) || r.teamId?.toLowerCase().includes(search.toLowerCase());
    const md = !deptFilter || r.department?.toLowerCase().includes(deptFilter.toLowerCase());
    const mst = !statusFilter || r.status === statusFilter;

    // Security/Role Filter: non-admin/non-hr users can only view requests they are responsible for
    const isAdminOrHr = ['admin', 'hr'].includes(user?.role);
    if (!isAdminOrHr) {
      if (user?.role === 'dept_head' || user?.role === 'production_head') {
        if (!isDeptVisibleToUser(r.department, r.department, user)) return false;
      } else {
        const isUserInChain = r.approvalChain?.some(step => step.role === user?.role);
        const sameDept = isDeptVisibleToUser(r.department, r.department, user);
        if (!isUserInChain && !sameDept) return false;
      }
    }

    return ms && md && mst;
  });

  const exportCSV = () => {
    const rows = filtered.map(r => ({
      'Request ID': r.requestId, 'Employee': r.employeeName, 'Team ID': r.teamId,
      'Department': r.department, 'Status': r.status, 'Submitted': formatDate(r.submittedAt),
      ...(activeReport === 'leave' ? { 'Leave Type': r.leaveType, 'Days': r.daysCount, 'From': formatDate(r.fromDate), 'To': formatDate(r.toDate) } : {}),
      ...(activeReport === 'early_leave' ? { 'Duration': r.earlyLeaveDuration, 'Leave Time': r.requestedLeaveTime } : {}),
      ...(activeReport === 'extra_work' ? { 'Work Type': r.workType, 'Hours': r.totalHours, 'Work Date': formatDate(r.workDate) } : {}),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `WRMS_${activeReport}_Report_${Date.now()}.xlsx`);
    toast.success('Exported to Excel');
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(14);
    doc.text(`Leave & Request ${getRequestTypeLabel(activeReport)} Report — Digi Sahyadri`, 14, 16);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()} | Total: ${filtered.length} records`, 14, 22);
    autoTable(doc, {
      startY: 28,
      head: [['Request ID', 'Employee', 'Team ID', 'Department', 'Status', 'Submitted']],
      body: filtered.map(r => [r.requestId, r.employeeName, r.teamId, r.department, r.status, formatDate(r.submittedAt)]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [79, 70, 229], textColor: 255 },
    });
    doc.save(`Request_${activeReport}_${Date.now()}.pdf`);
    toast.success('Exported to PDF');
  };

  // Summary stats
  const stats = {
    total: filtered.length,
    approved: filtered.filter(r => r.status === 'approved').length,
    pending: filtered.filter(r => r.status === 'pending').length,
    rejected: filtered.filter(r => r.status === 'rejected').length,
    cancelled: filtered.filter(r => r.status === 'cancelled').length,
  };

  const totalDays = filtered.filter(r => r.status === 'approved')
    .reduce((s, r) => s + (parseFloat(r.daysCount) || 0), 0);

  const totalHours = filtered.filter(r => r.status === 'approved')
    .reduce((s, r) => s + (parseFloat(r.totalHours) || 0), 0);

  return (
    <div className="d-flex wrms-page" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      <Sidebar />
      <div className="flex-grow-1 wrms-main-content" style={{ minWidth: 0, maxWidth: '100vw' }}>
        <Navbar />
        <div className="wrms-container">
          <div className="wrms-page-header">
            <div className="wrms-header-info">
              <div className="wrms-breadcrumb">Requests · <span>Reports & Analytics</span></div>
              <h1 className="wrms-page-title">Request Reports</h1>
              <p className="wrms-page-subtitle">Analyze request patterns, export data, and review departmental trends.</p>
            </div>
          </div>

          {/* Report Tab Selector */}
          <div className="wrms-report-tabs-grid">
            {REPORT_TABS.map(t => (
              <button key={t.id} className={`wrms-report-tab-btn ${activeReport === t.id ? 'active' : ''}`}
                onClick={() => setActiveReport(t.id)}>
                {t.label}
              </button>
            ))}
          </div>

          <div style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
            {/* Summary Stats */}
            <div className="wrms-stats-grid">
              <div className="wrms-stat-card stat-primary">
                <div className="wrms-stat-icon icon-primary"><FaClipboardList /></div>
                <div className="wrms-stat-number">{stats.total}</div>
                <div className="wrms-stat-label">Total Requests</div>
              </div>
              <div className="wrms-stat-card stat-success">
                <div className="wrms-stat-icon icon-success"><FaCheck /></div>
                <div className="wrms-stat-number">{stats.approved}</div>
                <div className="wrms-stat-label">Approved</div>
              </div>
              <div className="wrms-stat-card stat-warning">
                <div className="wrms-stat-icon icon-warning"><FaClock /></div>
                <div className="wrms-stat-number">{stats.pending}</div>
                <div className="wrms-stat-label">Pending</div>
              </div>
              {/* Rejected */}
              <div className="wrms-stat-card stat-danger">
                <div className="wrms-stat-icon icon-danger"><FaTimes /></div>
                <div className="wrms-stat-number">{stats.rejected}</div>
                <div className="wrms-stat-label">Rejected</div>
              </div>
              {/* Cancelled */}
              <div className="wrms-stat-card stat-cancelled">
                <div className="wrms-stat-icon icon-cancelled"><FaBan /></div>
                <div className="wrms-stat-number">{stats.cancelled}</div>
                <div className="wrms-stat-label">Cancelled</div>
              </div>
              {/* Extra cards per report type */}
              {activeReport === 'leave' && (
                <div className="wrms-stat-card stat-info">
                  <div className="wrms-stat-icon icon-info"><FaCalendarAlt /></div>
                  <div className="wrms-stat-number">{totalDays.toFixed(1)}</div>
                  <div className="wrms-stat-label">Total Leave Days</div>
                </div>
              )}
              {activeReport === 'extra_work' && (
                <div className="wrms-stat-card stat-orange">
                  <div className="wrms-stat-icon icon-warning"><FaClock /></div>
                  <div className="wrms-stat-number">{totalHours.toFixed(1)}h</div>
                  <div className="wrms-stat-label">Total Extra Hours</div>
                </div>
              )}
            </div>

            {/* Responsive Filters */}
            <div className="wrms-reports-filter-bar">
              <div className="wrms-reports-search-wrap">
                <FaSearch className="wrms-search-icon" />
                <input
                  className="wrms-form-control form-control"
                  placeholder="Search by name, Team ID..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>

              <div className="wrms-reports-select-row">
                <div className="wrms-reports-dept-wrap">
                  <input
                    className="wrms-form-control form-control"
                    placeholder="Filter by dept..."
                    value={deptFilter}
                    onChange={e => setDeptFilter(e.target.value)}
                  />
                </div>
                <div className="wrms-reports-status-wrap">
                  <select
                    className="wrms-form-control form-control form-select"
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="wrms-reports-actions-row">
                <button className="wrms-btn-outline" onClick={fetchData} title="Refresh report">
                  <FaSync size={11} />
                </button>
                <button className="wrms-btn-primary" onClick={exportCSV}>
                  <FaFileExport size={12} /> CSV
                </button>
                <button className="wrms-btn-primary wrms-btn-pdf" onClick={exportPDF}>
                  <FaFileExport size={12} /> PDF
                </button>
              </div>
            </div>

            {/* Records count & swipe hint */}
            <div className="d-flex justify-content-between align-items-center mb-3" style={{ fontSize: '0.8rem', color: '#64748b' }}>
              <div>
                Showing <strong>{filtered.length}</strong> of <strong>{requests.length}</strong> records
              </div>
              <div className="d-md-none text-primary" style={{ fontSize: '0.72rem', fontWeight: 600 }}>
                👉 Swipe table to view
              </div>
            </div>

              {/* Table */}
              <div className="wrms-table">
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    <div className="spinner-border text-primary" role="status" />
                    <p style={{ marginTop: '12px' }}>Loading report data...</p>
                  </div>
                ) : (
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Request ID</th>
                        <th>Employee</th>
                        <th>Team ID</th>
                        <th>Department</th>
                        {activeReport === 'leave' && <><th>Leave Type</th><th>Days</th></>}
                        {activeReport === 'early_leave' && <><th>Duration</th><th>Leave Time</th></>}
                        {activeReport === 'gate_pass' && <><th>Time Out</th><th>Return</th></>}
                        {activeReport === 'extra_work' && <><th>Work Type</th><th>Hours</th></>}
                        <th>Status</th>
                        <th>Submitted</th>
                        <th style={{ textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr><td colSpan={11} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                          No records found for this report.
                        </td></tr>
                      ) : filtered.map(r => (
                        <tr key={r.id}>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#4f46e5', fontWeight: 700 }}>
                            <span 
                              onClick={() => navigate(`/wrms/request/${r.requestType}/${r.id}`)}
                              style={{ cursor: 'pointer', textDecoration: 'underline' }}
                              title="Click to track details"
                            >
                              {r.requestId}
                            </span>
                          </td>
                          <td style={{ fontWeight: 600, fontSize: '0.85rem' }}>{r.employeeName}</td>
                          <td style={{ fontSize: '0.75rem', color: '#64748b' }}>{r.teamId}</td>
                          <td style={{ fontSize: '0.78rem' }}>{r.department}</td>
                          {activeReport === 'leave' && (
                            <>
                              <td style={{ fontSize: '0.78rem', textTransform: 'capitalize' }}>{r.leaveType?.replace(/_/g, ' ')}</td>
                              <td style={{ fontWeight: 700 }}>{r.daysCount}</td>
                            </>
                          )}
                          {activeReport === 'early_leave' && (
                            <>
                              <td style={{ fontSize: '0.78rem' }}>{r.earlyLeaveDuration}</td>
                              <td style={{ fontSize: '0.78rem' }}>{r.requestedLeaveTime}</td>
                            </>
                          )}
                          {activeReport === 'gate_pass' && (
                            <>
                              <td style={{ fontSize: '0.78rem' }}>{r.timeOut}</td>
                              <td style={{ fontSize: '0.78rem' }}>
                                <span>{r.expectedTimeIn || r.timeIn || '—'}</span>
                                {r.isTimeInEdited && (
                                  <span
                                    title={r.timeInUpdatedByName ? `Edited by ${r.timeInUpdatedByName}` : 'Edited'}
                                    style={{
                                      marginLeft: '6px',
                                      fontSize: '0.65rem',
                                      color: '#b45309',
                                      background: '#fef3c7',
                                      border: '1px solid #fde68a',
                                      padding: '1px 5px',
                                      borderRadius: '4px',
                                      fontWeight: 700
                                    }}
                                  >
                                    ✏️ Edited
                                  </span>
                                )}
                              </td>
                            </>
                          )}
                          {activeReport === 'extra_work' && (
                            <>
                              <td style={{ fontSize: '0.78rem', textTransform: 'capitalize' }}>{r.workType?.replace(/_/g, ' ')}</td>
                              <td style={{ fontWeight: 700 }}>{r.totalHours}h</td>
                            </>
                          )}
                          <td>
                            <span style={{
                              padding: '3px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 700,
                              background: r.status === 'approved' ? '#ecfdf5' : r.status === 'rejected' ? '#fef2f2' : '#fffbeb',
                              color: r.status === 'approved' ? '#065f46' : r.status === 'rejected' ? '#991b1b' : '#92400e',
                            }}>
                              {r.status}
                            </span>
                          </td>
                          <td style={{ fontSize: '0.78rem', color: '#64748b' }}>{formatDate(r.submittedAt)}</td>
                          <td style={{ textAlign: 'center' }}>
                            <button className="wrms-btn-outline"
                              onClick={() => navigate(`/wrms/request/${r.requestType}/${r.id}`)}
                              style={{ padding: '4px 10px', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '4px', margin: '0 auto' }}>
                              <FaExternalLinkAlt size={9} /> View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WRMSReportsPage;
